/**
 * Basemap Session Utility
 *
 * Manages ArcGIS basemap sessions for optimized tile usage.
 * Sessions allow unlimited tile access for up to 12 hours with a single charge.
 *
 * Key Features:
 * - Create new sessions with API key
 * - Store/retrieve sessions from localStorage
 * - Validate session expiry
 * - Automatic cleanup of expired sessions
 */

import {
  SESSION_STORAGE_KEY,
  SESSION_DURATION_SECONDS,
  SESSION_STYLE_FAMILY
} from 'constants/basemap.constants';
import { debugLog, debugWarn, debugError } from './basemap-debug.util';

export interface BasemapSession {
  sessionToken: string;
  sessionId: string;
  startTime: number;
  endTime: number;
  accessToken: string;
  createdAt: number;
}

interface SessionResponse {
  sessionToken: string;
  sessionId?: string;
  startTime: number;
  endTime: number;
}

const BASEMAP_SESSION_API_URL =
  'https://basemapstyles-api.arcgis.com/arcgis/rest/services/styles/v2/sessions/start';

/**
 * Creates a new basemap session using the provided API key
 * @param accessToken - ArcGIS API key with premium:user:basemaps privilege
 * @returns Promise<BasemapSession> - Session data including token and expiry
 */
export function createBasemapSession(
  accessToken: string
): Promise<BasemapSession> {
  return new Promise(function (resolve, reject) {
    const url =
      BASEMAP_SESSION_API_URL +
      '?' +
      'styleFamily=' +
      SESSION_STYLE_FAMILY +
      '&durationSeconds=' +
      SESSION_DURATION_SECONDS;

    fetch(url, {
      method: 'GET',
      headers: {
        Authorization: 'Bearer ' + accessToken
      }
    })
      .then(function (response) {
        if (!response.ok) {
          return response.text().then(function (text) {
            throw new Error(
              'HTTP error! status: ' + response.status + ', body: ' + text
            );
          });
        }
        return response.json();
      })
      .then(function (session: SessionResponse) {
        if (!session || !session.sessionToken) {
          throw new Error('Invalid session response: missing sessionToken');
        }

        const basemapSession: BasemapSession = {
          sessionToken: session.sessionToken,
          sessionId: session.sessionId || 'N/A',
          startTime: session.startTime,
          endTime: session.endTime,
          accessToken: accessToken,
          createdAt: Date.now()
        };

        // Store in localStorage
        try {
          localStorage.setItem(
            SESSION_STORAGE_KEY,
            JSON.stringify(basemapSession)
          );
        } catch (e) {
          debugWarn('Failed to store basemap session in localStorage:', e);
        }

        debugLog('Basemap session created successfully');
        resolve(basemapSession);
      })
      .catch(function (error) {
        debugError('Error creating basemap session:', error);
        reject(error);
      });
  });
}

/**
 * Retrieves stored session from localStorage
 * @returns BasemapSession | null - Stored session or null if not found/invalid
 */
export function getStoredSession(): BasemapSession | null {
  try {
    const stored = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const session: BasemapSession = JSON.parse(stored);

    // Validate session structure
    if (
      !session.sessionToken ||
      !session.endTime ||
      typeof session.endTime !== 'number'
    ) {
      debugWarn('Invalid session structure, clearing session');
      clearSession();
      return null;
    }

    return session;
  } catch (e) {
    debugError('Error retrieving stored session:', e);
    clearSession();
    return null;
  }
}

/**
 * Checks if a session is still valid (not expired)
 * @param session - Session to validate
 * @returns boolean - True if session is valid, false if expired
 */
export function isSessionValid(session: BasemapSession | null): boolean {
  if (!session) {
    return false;
  }

  const now = Date.now();
  const isValid = now < session.endTime;

  if (!isValid) {
    debugLog('Basemap session has expired');
  }

  return isValid;
}

/**
 * Clears stored session from localStorage
 */
export function clearSession(): void {
  try {
    localStorage.removeItem(SESSION_STORAGE_KEY);
    debugLog('Basemap session cleared');
  } catch (e) {
    debugError('Error clearing session:', e);
  }
}

/**
 * Gets time remaining in session (in milliseconds)
 * @param session - Session to check
 * @returns number - Milliseconds remaining, or 0 if expired
 */
export function getSessionTimeRemaining(session: BasemapSession): number {
  if (!session) {
    return 0;
  }

  const now = Date.now();
  const remaining = session.endTime - now;

  return remaining > 0 ? remaining : 0;
}

/**
 * Checks if session needs refresh (less than 5 minutes remaining)
 * @param session - Session to check
 * @returns boolean - True if session should be refreshed
 */
export function shouldRefreshSession(session: BasemapSession): boolean {
  const FIVE_MINUTES = 5 * 60 * 1000;
  const remaining = getSessionTimeRemaining(session);

  return remaining > 0 && remaining < FIVE_MINUTES;
}
